import { PurchaseOrder, POStatus } from "@po-management/types";
import { usePOs } from "./hooks/usePOs";
import { useCurrentUser } from "../auth/useCurrentUser";

const STATUS_LABELS: Record<POStatus, string> = {
  Draft: "Drafts",
  PendingApproval: "Pending Approval",
  Approved: "Approved",
  Sent: "Sent",
  Received: "Received",
  Rejected: "Rejected",
  Void: "Void",
};

const STATUS_ORDER: POStatus[] = ["Draft", "PendingApproval", "Approved", "Sent", "Received", "Rejected", "Void"];

const STATUS_COLORS: Record<POStatus, string> = {
  Draft: "#6b7280",
  PendingApproval: "#d97706",
  Approved: "#059669",
  Sent: "#2563eb",
  Received: "#7c3aed",
  Rejected: "#dc2626",
  Void: "#9ca3af",
};

function StatusBadge({ status }: { status: POStatus }): JSX.Element {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11,
      background: `${STATUS_COLORS[status]}20`, color: STATUS_COLORS[status], fontWeight: 600,
    }}>
      {status === "PendingApproval" ? "Pending Approval" : status}
    </span>
  );
}

interface PORowProps {
  po: PurchaseOrder;
  onEdit: (id: string) => void;
  onView: (id: string) => void;
}

function PORow({ po, onEdit, onView }: PORowProps): JSX.Element {
  const isDraft = po.status === "Draft" || po.status === "Rejected";

  return (
    <tr
      onClick={() => isDraft ? onEdit(po.id) : onView(po.id)}
      style={{ cursor: "pointer", borderBottom: "1px solid #eee" }}
    >
      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: "#5B6A8A" }}>{po.poNumber}</td>
      <td style={{ padding: "10px 12px", fontSize: 13 }}>{po.vendorNameSnapshot}</td>
      <td style={{ padding: "10px 12px", fontSize: 13, textAlign: "right" }}>€ {Number(po.totalValue).toFixed(2)}</td>
      <td style={{ padding: "10px 12px", fontSize: 13 }}>{String(po.documentDate).slice(0, 10)}</td>
      <td style={{ padding: "10px 12px" }}><StatusBadge status={po.status as POStatus} /></td>
      <td style={{ padding: "10px 12px", fontSize: 12, color: "#888" }}>{po.assignedApproverName ?? "—"}</td>
      <td style={{ padding: "10px 12px" }}>
        {isDraft && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(po.id); }}
            style={{ fontSize: 12, padding: "4px 10px", background: "#f5f6fa", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
          >
            Edit
          </button>
        )}
        {!isDraft && (
          <button
            onClick={(e) => { e.stopPropagation(); onView(po.id); }}
            style={{ fontSize: 12, padding: "4px 10px", background: "#f5f6fa", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
          >
            View
          </button>
        )}
      </td>
    </tr>
  );
}

interface DashboardProps {
  onNewPO: () => void;
  onEditPO: (id: string) => void;
  onViewPO: (id: string) => void;
}

export function Dashboard({ onNewPO, onEditPO, onViewPO }: DashboardProps): JSX.Element {
  const currentUser = useCurrentUser();
  const { pos, loading, error, refetch } = usePOs();

  const grouped = STATUS_ORDER.reduce<Map<POStatus, PurchaseOrder[]>>((map, s) => {
    const items = pos.filter((p) => p.status === s);
    if (items.length > 0) map.set(s, items);
    return map;
  }, new Map());

  return (
    <div style={{ padding: "2rem", fontFamily: "sans-serif", maxWidth: 1000 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: "#5B6A8A" }}>Purchase Orders</h2>
          {currentUser && <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>{currentUser.name}</div>}
        </div>
        <button
          onClick={onNewPO}
          style={{ padding: "9px 20px", background: "#5B6A8A", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 500 }}
        >
          + New PO
        </button>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", background: "#fff0f0", border: "1px solid #fcc", borderRadius: 4, color: "#c00", marginBottom: 16, fontSize: 13 }}>
          {error} <button onClick={refetch} style={{ marginLeft: 8, background: "none", border: "none", color: "#5B6A8A", cursor: "pointer", textDecoration: "underline" }}>Retry</button>
        </div>
      )}

      {loading && <p style={{ color: "#888" }}>Loading...</p>}

      {!loading && pos.length === 0 && !error && (
        <div style={{ textAlign: "center", padding: "3rem", color: "#888" }}>
          <p>No purchase orders yet.</p>
          <button
            onClick={onNewPO}
            style={{ padding: "9px 20px", background: "#5B6A8A", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
          >
            Create your first PO
          </button>
        </div>
      )}

      {Array.from(grouped.entries()).map(([status, items]) => (
        <div key={status} style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 14, color: "#5B6A8A", margin: "0 0 8px" }}>
            {STATUS_LABELS[status]} <span style={{ fontWeight: 400, color: "#999" }}>({items.length})</span>
          </h3>
          <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 6, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            <thead>
              <tr style={{ background: "#f5f6fa", fontSize: 12, color: "#666" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600 }}>PO Number</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600 }}>Vendor</th>
                <th style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600 }}>Total Value</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600 }}>Date</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600 }}>Status</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600 }}>Approver</th>
                <th style={{ padding: "8px 12px" }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((po) => <PORow key={po.id} po={po} onEdit={onEditPO} onView={onViewPO} />)}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
